import { useState } from "react";

/**
 * Shared drag-to-reorder mechanics — native HTML5 drag-and-drop plus an
 * Up/Down button fallback (touch devices and keyboard users can't use
 * native drag at all), for a locally-known, already-ordered list of items.
 * The caller owns persistence: both `dragRowProps(id).onDrop` and
 * `moveByOffset` only compute the new order and hand it to `onCommit` —
 * this hook has no idea whether that's a personal branch order or a
 * personal step order.
 *
 * Extracted out of FlowPlayerPage's branch-reorder controls and
 * StepOrderDialog's step-reorder controls, which independently
 * reimplemented the exact same state/handlers (including the Firefox
 * `dataTransfer.setData` requirement and the drag-cancel `onDragEnd`
 * cleanup — both already debugged once in the branch-reorder version).
 */
export function useDragReorder<T extends { id: string }>({
  items,
  onCommit,
  disabled = false,
}: {
  /** Current order, used to compute the new order on drop/move. */
  items: T[];
  /** Called with the full new order of ids once a drag or arrow-click
   * actually changes the order. */
  onCommit: (newOrder: string[]) => void;
  /** Blocks starting a new drag or arrow-move — e.g. while a previous
   * reorder is still saving, so a second move can't race ahead of it and
   * get silently overwritten once both requests land (there's no
   * optimistic update or request sequencing beyond this). */
  disabled?: boolean;
}) {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const canReorder = !disabled && items.length > 1;

  const clear = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const commit = (reordered: T[]) => onCommit(reordered.map((it) => it.id));

  const moveByOffset = (id: string, direction: -1 | 1) => {
    if (disabled) return;
    const idx = items.findIndex((it) => it.id === id);
    const targetIdx = idx + direction;
    if (idx === -1 || targetIdx < 0 || targetIdx >= items.length) return;
    const reordered = [...items];
    [reordered[idx], reordered[targetIdx]] = [reordered[targetIdx], reordered[idx]];
    commit(reordered);
  };

  const dragRowProps = (id: string) => ({
    draggable: canReorder,
    onDragStart: (e: React.DragEvent) => {
      if (!canReorder) return;
      e.dataTransfer.effectAllowed = "move";
      // Firefox refuses to start a drag at all unless data is set on it —
      // Chrome/Edge don't need this, but it's free here.
      e.dataTransfer.setData("text/plain", id);
      setDraggedId(id);
    },
    onDragOver: (e: React.DragEvent) => {
      if (!canReorder) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOverId(id);
    },
    onDragLeave: () => setDragOverId((cur) => (cur === id ? null : cur)),
    onDrop: () => {
      if (draggedId && draggedId !== id) {
        const fromIdx = items.findIndex((it) => it.id === draggedId);
        const toIdx = items.findIndex((it) => it.id === id);
        if (fromIdx !== -1 && toIdx !== -1) {
          const reordered = [...items];
          const [moved] = reordered.splice(fromIdx, 1);
          reordered.splice(toIdx, 0, moved);
          commit(reordered);
        }
      }
      clear();
    },
    // Fires even when the drag is cancelled (dropped outside any row, Esc
    // mid-drag) — `onDrop` above is the only other place this clears, and
    // it never fires in that case, so without this the row stays stuck
    // rendered at reduced opacity.
    onDragEnd: clear,
    isDragging: draggedId === id,
    isDragOver: dragOverId === id && !!draggedId && draggedId !== id,
  });

  return { dragRowProps, moveByOffset, canReorder, clear };
}
